import { companionKeys } from '@/hooks/queries/companion-keys';
import { getNotificationsTransmitClient } from '@/libs/notifications-transmit';
import {
  companionBrandChannel,
  CompanionRealtimeEventName,
  type CompanionRealtimeEvent,
} from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

/**
 * Keeps the panel honest between two refetches.
 *
 * The event is a wake-up and carries no card: the feed is read back through the
 * REST endpoint, so a subscriber can never end up rendering a payload the
 * authorization layer did not gate. Nothing is torn down beyond this
 * subscription — the transmit client is shared with the notifications bridge,
 * and closing it here would take the bell down with it.
 */

const SUBSCRIBE_TIMEOUT_MS = 10_000;

interface CompanionTransmitBridgeProps {
  brandId: string | null | undefined;
}

export function CompanionTransmitBridge({ brandId }: CompanionTransmitBridgeProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!brandId) return;

    let cancelled = false;
    const transmit = getNotificationsTransmitClient();
    const subscription = transmit.subscription(companionBrandChannel(brandId));

    subscription.onMessage((event: CompanionRealtimeEvent) => {
      if (event?.event !== CompanionRealtimeEventName.FEED_CHANGED) return;
      queryClient.invalidateQueries({ queryKey: companionKeys.brand(brandId) });
    });

    const subscribe = async () => {
      try {
        await Promise.race([
          subscription.create(),
          new Promise((_, reject) =>
            window.setTimeout(
              () => reject(new Error('Companion subscription timed out')),
              SUBSCRIBE_TIMEOUT_MS
            )
          ),
        ]);

        if (!cancelled && !subscription.isCreated) {
          console.warn('[COMPANION] Companion feed subscription was not authorized');
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[COMPANION] Failed to subscribe to companion feed events', error);
        }
      }
    };

    subscribe();

    return () => {
      cancelled = true;
      subscription.delete().catch((error) => {
        console.warn('[COMPANION] Failed to unsubscribe from companion feed events', error);
      });
    };
  }, [brandId, queryClient]);

  return null;
}
